import { Suspense, lazy, useEffect, useRef } from 'react';
import HomeInfoCards from '../HomeInfoCards';
import styles from './HomePage.module.scss';
import HomeStaticBlock from '../HomeStaticBlock';
import HomeTodayHero from '../HomeToday/HomeTodayHero';
import { useDispatch, useSelector } from 'react-redux';
import { homeActions } from '../../store';
import {
  getActiveSalonId,
  getActiveSalon,
  getActiveSalonHideHomeStats,
} from '../../../Auth/store/selectors';
import useWindowSize from '../../../../utils/useWindowSize';
import clx from 'classnames';
import BannerBlock from '../BannerBlock';
import ViggoBanner from '../ViggoBanner';
import SubscriptionRenewalBanner from '../SubscriptionRenewalBanner';
import { ROUTES } from '../../../../routes/constants';
import { getActiveSalonToken } from 'utils/salonToken';
import blankPayBg from 'assets/images/payments_info/1.png';
import background from 'assets/images/home/background1.jpg';
import backgroundMbl from 'assets/images/home/background1_mbl.jpg';
import { getInvitiOnboarding } from '../../../Auth/store/selectors';
import { useNavigate } from 'react-router-dom';
import { getHomeStatistic } from 'Containers/Home/store/selectors';
import { getCustomerProducts } from 'Containers/Settings/store/selectors';
import BannerVideo from '../BannerVideo';
import coverTiktokVideo from '../../../../assets/images/home/coverTiktokVideo.png';
import { useTranslation } from 'react-i18next';
import { sendMessageToApp } from 'utils/exsternalCall';
import dayjs from 'dayjs';
import { HomeTablesSkeleton } from '../HomeSkeletons';
import useHomeQueuePolling from '../../hooks/useHomeQueuePolling';
import { isDemoMode } from 'demo';
import { isLightClient } from 'utils/lowMemory';

const HomeTables = lazy(() => import('../HomeTables'));
const Onboarding = lazy(() => import('../Onboarding'));
const HomeStoriesProposals = lazy(() => import('../HomeStoriesProposals'));

const SKIP_LOADER = { skipLoader: true };

const Home = () => {
  const dispatch = useDispatch();
  const activeSalon = useSelector(getActiveSalon());
  const activeSalonId = useSelector(getActiveSalonId());
  const hideHomeStats = useSelector(getActiveSalonHideHomeStats());
  const { isMobile } = useWindowSize();
  const homeStat = useSelector(getHomeStatistic());
  const userInvited = useSelector(getInvitiOnboarding());
  const products = useSelector(getCustomerProducts());
  const token = getActiveSalonToken();
  const navigate = useNavigate();
  const fromApp = !!localStorage.getItem('fromApp');
  const { t } = useTranslation();

  const demoMode = isDemoMode(activeSalon);

  const showOnboarding =
    !demoMode &&
    !dayjs(activeSalon?.data_iscrizione).isBefore(dayjs().subtract(2, 'month')) &&
    (!activeSalon?.idCostumerStripe || !activeSalon?.iban || activeSalon?.freeDays === 0);

  const showOnboardingRef = useRef(showOnboarding);
  showOnboardingRef.current = showOnboarding;

  useHomeQueuePolling(activeSalonId);

  // Stagger post-login/cambio salone: non far partire la coda
  // nello stesso istante di getDaysLeft (App / setActiveSalon).
  // Niente getCalendario in Home (resta solo nello Scheduler).
  useEffect(() => {
    if (!activeSalonId) return undefined;

    if (!hideHomeStats) {
      dispatch(homeActions.GET_HOME_STAT.REQUEST({}, null, SKIP_LOADER));
    }
    sendMessageToApp('app:notifications', {
      salonId: activeSalonId,
    });

    let queueTimerId;
    let onboardingTimerId;

    queueTimerId = window.setTimeout(() => {
      dispatch(homeActions.GET_QUEUE.REQUEST({}, null, SKIP_LOADER));
    }, 1000);

    if (showOnboardingRef.current) {
      onboardingTimerId = window.setTimeout(() => {
        dispatch(homeActions.GET_ONBOARDING.REQUEST({}, null, SKIP_LOADER));
      }, 1500);
    }

    return () => {
      if (queueTimerId != null) window.clearTimeout(queueTimerId);
      if (onboardingTimerId != null) window.clearTimeout(onboardingTimerId);
    };
  }, [dispatch, activeSalonId, hideHomeStats, demoMode]);

  function openFlutterContact() {
    sendMessageToApp('app:onboardingContact', {
      action: 'open',
      token,
      invitiRimanenti: userInvited,
      minimum: userInvited == 50 ? 30 : 5,
    });
  }

  useEffect(() => {
    const inviteUsers = async () => {
      if (userInvited > 19 && homeStat.allClienti < 50) openFlutterContact();
    };
    if (localStorage.getItem('fromApp')) inviteUsers();
  }, []);

  const handlePressTikTok = () => {
    const tiktok = 'https://vm.tiktok.com/ZGegAWqFX/';

    if (!fromApp) window.open(tiktok, '_blank');
    else sendMessageToApp('app:url', { url: tiktok });
  };

  const daysLeft = Number(products?.giorniRimanenti);
  const showRenewalBanner = Number.isFinite(daysLeft) && daysLeft > 0 && daysLeft <= 3;

  return (
    <div className={clx(styles.home_container, { [styles['home_container_mobile']]: isMobile })}>
      {showRenewalBanner ? (
        <SubscriptionRenewalBanner
          daysLeft={daysLeft}
          expiryDate={products?.scadenza}
        />
      ) : null}
      <ViggoBanner />
      <HomeStaticBlock showPrivateApp />
      <HomeTodayHero />
      {!isMobile && <HomeInfoCards />}
      {!<BannerVideo
        cover={coverTiktokVideo}
        video={'https://zb-img.s3.eu-west-3.amazonaws.com/video/video+tik+tok+(1).mp4'}
        title={t('home.bannerVideo.title')}
        description={t('home.bannerVideo.description')}
        action={t('home.bannerVideo.action')}
        onClick={() => handlePressTikTok()}
      />}
      {!<BannerBlock
        action={t('home.bannerBlock.signup.action')}
        background={isMobile ? backgroundMbl : background}
        description={t('home.bannerBlock.signup.description')}
        onClick={() => {
          window.open(
            `https://v0-zeta-barber-back-office-wi.vercel.app/f/61c8e3f5f3c88247ce2a9bee`,
            '_blank',
          );
        }}
      />}
      {showOnboarding ? (
        <Suspense fallback={null}>
          <Onboarding />
        </Suspense>
      ) : null}
      <Suspense fallback={<HomeTablesSkeleton isMobile={isMobile} />}>
        <HomeTables afterQueue={isMobile ? <HomeInfoCards /> : null} />
      </Suspense>
      {!isLightClient() ? (
        <Suspense fallback={null}>
          <HomeStoriesProposals />
        </Suspense>
      ) : null}
      {!<BannerBlock
        title={t('home.bannerBlock.innovation.title')}
        subtitle={t('home.bannerBlock.innovation.subtitle')}
        description={t('home.bannerBlock.innovation.description')}
        action={t('common.actions.next')}
        img={blankPayBg}
        onClick={() => {
          navigate(`${ROUTES.PAYMENTS}${ROUTES.INFORMATIONS}`);
        }}
      />}
      <HomeStaticBlock showShop />
    </div>
  );
};

export default Home;
